<script setup lang="ts">
import { PhCaretDown, PhX } from '@phosphor-icons/vue';
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  type PropType,
  ref,
} from 'vue';
import { useI18n } from 'vue-i18n';

export interface DropDownItem {
  value: string;
  text: string;
}

const { t } = useI18n();

const props = defineProps({
  items: {
    type: Array as () => Array<DropDownItem>,
    required: true,
  },
  label: {
    type: String,
    required: false,
  },
  modelValue: {
    type: [String, Number, null] as PropType<string | number | null>,
    default: '',
  },
  disabled: {
    type: Boolean,
    required: false,
    default: false,
  },
  up: {
    type: Boolean,
    required: false,
    default: false,
  },
  teleport: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue']);

const dropdownOpen = ref(false);
const searchQuery = ref('');
const searchInputRef = ref<HTMLInputElement | null>(null);
const highlightedIndex = ref(-1);
const buttonRef = ref<HTMLButtonElement | null>(null);
const menuFixedStyle = ref<Record<string, string>>({});

const updateMenuPosition = () => {
  if (!buttonRef.value) return;
  const rect = buttonRef.value.getBoundingClientRect();
  if (props.up) {
    menuFixedStyle.value = {
      bottom: `${window.innerHeight - rect.top + 2}px`,
      left: `${rect.left}px`,
      width: `${rect.width}px`,
    };
  } else {
    menuFixedStyle.value = {
      top: `${rect.bottom + 2}px`,
      left: `${rect.left}px`,
      width: `${rect.width}px`,
    };
  }
};

const filteredItems = computed(() => {
  if (!searchQuery.value.trim()) {
    return props.items;
  }
  const query = searchQuery.value.toLowerCase();
  return props.items.filter(
    (item) =>
      item.text.toLowerCase().includes(query) ||
      item.value.toLowerCase().includes(query),
  );
});

const selectedLabel = computed(() => {
  const selectedItem = props.items.find(
    (item) => item.value == props.modelValue,
  );
  return selectedItem ? selectedItem.text : props.label;
});

const toggleDropdown = async () => {
  if (props.disabled) return;
  dropdownOpen.value = !dropdownOpen.value;

  if (dropdownOpen.value) {
    if (props.teleport) updateMenuPosition();
    await nextTick();
    searchInputRef.value?.focus();
  }
  highlightedIndex.value = -1;
};

const selectItem = (item: DropDownItem) => {
  emit('update:modelValue', item.value);
  dropdownOpen.value = false;
};

const clearSelection = () => {
  emit('update:modelValue', '');
  searchQuery.value = '';
  highlightedIndex.value = -1;
};

const handleKeydown = (event: KeyboardEvent) => {
  if (!dropdownOpen.value) return;

  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault();
      if (highlightedIndex.value < filteredItems.value.length - 1) {
        highlightedIndex.value++;
      } else {
        highlightedIndex.value = 0;
      }
      break;

    case 'ArrowUp':
      event.preventDefault();
      if (highlightedIndex.value > 0) {
        highlightedIndex.value--;
      } else {
        highlightedIndex.value = filteredItems.value.length - 1;
      }
      break;

    case 'Enter':
      event.preventDefault();
      if (
        highlightedIndex.value >= 0 &&
        filteredItems.value[highlightedIndex.value]
      ) {
        selectItem(filteredItems.value[highlightedIndex.value]);
      }
      break;

    case 'Escape':
      event.preventDefault();
      dropdownOpen.value = false;
      break;
  }
};

window.addEventListener('click', (e) => {
  if (
    !(e.target as Element).closest('.dropdown') &&
    !(e.target as Element).closest('.dropdown-teleport-menu')
  ) {
    dropdownOpen.value = false;
  }
});

window.addEventListener('keydown', (e) => {
  handleKeydown(e);
});

const handleScroll = () => {
  if (dropdownOpen.value && props.teleport) {
    updateMenuPosition();
  }
};

onMounted(() => window.addEventListener('scroll', handleScroll, true));
onUnmounted(() => window.removeEventListener('scroll', handleScroll, true));
</script>

<template>
  <div
    class="dropdown"
    :class="{
      'is-active': dropdownOpen,
      'is-disabled': props.disabled,
      'is-up': props.up,
    }"
    @click.prevent.stop="toggleDropdown"
  >
    <div class="dropdown-trigger">
      <button
        ref="buttonRef"
        class="button"
        type="button"
        :disabled="props.disabled"
      >
        <span>{{ selectedLabel }}</span>
        <div class="actions" v-if="!props.disabled">
          <PhX
            class="dropdown-clear"
            weight="bold"
            @click.prevent.stop="clearSelection"
            v-if="modelValue"
          />
          <PhCaretDown weight="bold" class="dropdown-chevron" />
        </div>
      </button>
    </div>
    <Teleport to="body" :disabled="!props.teleport">
      <div
        v-if="dropdownOpen"
        :class="props.teleport ? 'dropdown-teleport-menu' : 'dropdown-menu'"
        :style="props.teleport ? menuFixedStyle : undefined"
      >
        <div class="dropdown-content">
          <div class="dropdown-search">
            <input
              ref="searchInputRef"
              type="text"
              class="input"
              :placeholder="t('search') + '...'"
              data-test="search-input"
              v-model="searchQuery"
              @click.stop
            />
          </div>
          <div v-if="filteredItems.length === 0" class="pl-4">
            {{ t('noItemsFound') }}
          </div>
          <div class="dropdown-items">
            <a
              v-for="(item, index) in filteredItems"
              :key="item.value"
              class="dropdown-item"
              :class="{ 'is-highlighted': index === highlightedIndex }"
              @click.prevent.stop="selectItem(item)"
              :data-test="'item-' + item.value"
            >
              {{ item.text }}
            </a>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style lang="scss" scoped>
.dropdown {
  width: 100%;
  position: relative;

  &.is-disabled {
    .button:disabled {
      cursor: default;
      box-shadow: none;
      opacity: 1;

      span {
        color: inherit;
        opacity: 1;
        -webkit-text-fill-color: currentColor;
      }

      &:hover {
        box-shadow: none;
      }

      &:active {
        box-shadow: none;
        transform: none;
      }
    }
  }

  .dropdown-menu {
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    z-index: 100;
    padding-top: 2px;

    .dropdown-content {
      .dropdown-items {
        max-height: 20rem;
        overflow-y: auto;
      }
    }
  }

  .dropdown-trigger {
    width: 100%;

    .button {
      width: 100%;
      height: var(--bulma-control-height);

      position: relative;
      display: flex;
      align-items: center;
      justify-content: flex-start;

      padding-right: 3.5rem;

      .actions {
        position: absolute;
        right: 0.5rem;
        top: 0;
        height: 100%;

        display: flex;
        align-items: center;
        justify-content: flex-end;

        .dropdown-chevron {
          transition: transform 0.2s;
        }
        .dropdown-clear {
          margin-right: 0.25rem;
          padding: 0.25rem;
          transition: background-color 0.2s;

          &:hover {
            background-color: var(--table-red);
          }
        }
      }

      span {
        width: 100%;
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
        text-align: start;
      }
    }
  }

  &.is-active {
    .dropdown-chevron {
      transform: rotate(180deg);
    }
  }

  .dropdown-content {
    .dropdown-search {
      padding: 0.5rem;
      .input {
        width: 100%;
        padding: 0.5rem;
        border: 1px solid #ddd;
        border-radius: 4px;
        margin-bottom: 0.5rem;
      }
    }

    .dropdown-item {
      padding: 0.5rem;
      cursor: pointer;

      &.is-highlighted {
        background-color: var(--hover-table-row-color);
      }
    }

    .dropdown-empty {
      padding: 0.5rem;
      color: #999;
      text-align: center;
      font-style: italic;
    }
  }
}
</style>

<style lang="scss">
.dropdown-teleport-menu {
  position: fixed;
  z-index: 1000;
  background: white;
  border-radius: 4px;
  box-shadow:
    0 8px 16px rgba(10, 10, 10, 0.1),
    0 0 0 1px rgba(10, 10, 10, 0.1);

  .dropdown-items {
    max-height: 20rem;
    overflow-y: auto;
  }

  .dropdown-search {
    padding: 0.5rem;

    .input {
      width: 100%;
      padding: 0.5rem;
      border: 1px solid #ddd;
      border-radius: 4px;
      margin-bottom: 0.5rem;
    }
  }

  .dropdown-item {
    display: block;
    padding: 0.5rem 1rem;
    cursor: pointer;
    color: #4a4a4a;
    font-size: 0.875rem;

    &:hover,
    &.is-highlighted {
      background-color: var(--hover-table-row-color, #f5f5f5);
    }
  }

  .pl-4 {
    padding: 0.5rem 1rem;
    color: #999;
    font-size: 0.875rem;
  }
}
</style>
